/**
 * Cart drawer discount code applier.
 *
 * Strategy: Use the /discount/{code} URL which Shopify auto-applies and redirects.
 * To stay on the same page (cart-drawer context), we hit it in the background via a hidden
 * fetch + a follow-up to /cart.js to refresh, then re-render the cart drawer using
 * Shopify's Section Rendering API.
 *
 * Fallback: if anything fails, redirect to /discount/{code}?redirect=/cart
 */
(function () {
  if (window.__cartDiscountInit) return;
  window.__cartDiscountInit = true;

  function init() {
    document.body.addEventListener('submit', handleSubmit, true);
  }

  async function handleSubmit(e) {
    const form = e.target.closest('[data-cart-discount-form]');
    if (!form) return;
    e.preventDefault();
    e.stopPropagation();

    const input = form.querySelector('input[name="discount"]');
    const button = form.querySelector('button[type="submit"]');
    const message = form.querySelector('[data-cart-discount-message]');
    const code = (input?.value || '').trim();

    if (!code) {
      showMessage(message, 'Please enter a discount code.', 'error');
      return;
    }

    button.disabled = true;
    button.dataset.originalText = button.dataset.originalText || button.textContent;
    button.textContent = 'Applying…';
    showMessage(message, '', 'clear');

    try {
      // Step 1: Apply discount via Shopify's /discount endpoint.
      // This sets the cart's discount cookie. We follow without redirect.
      await fetch(`/discount/${encodeURIComponent(code)}`, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'follow',
      });

      // Step 2: Trigger a cart refresh by hitting /cart/update with no changes.
      // This forces Shopify to recompute discounts against current cart.
      const updateRes = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: { _discount_attempt: Date.now().toString() } }),
      });

      if (!updateRes.ok) throw new Error('Cart update failed');

      // Step 3: Fetch current cart state to verify discount applied.
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await cartRes.json();

      const applied = (cart.cart_level_discount_applications || []).some(
        (d) => (d.title || '').toUpperCase() === code.toUpperCase()
      ) || (cart.discount_codes || []).some(
        (d) => (d.code || '').toUpperCase() === code.toUpperCase() && d.applicable !== false
      );

      if (applied || cart.total_discount > 0) {
        showMessage(message, `Code "${code}" applied.`, 'success');
        if (input) input.value = '';
        // Re-render cart drawer via Section Rendering API
        await refreshCartDrawer();
      } else {
        showMessage(message, `Code "${code}" is not valid or not applicable.`, 'error');
      }
    } catch (err) {
      console.error('[cart-discount]', err);
      // Fallback: full-page redirect approach.
      window.location.href = `/discount/${encodeURIComponent(code)}?redirect=/cart`;
      return;
    } finally {
      button.disabled = false;
      button.textContent = button.dataset.originalText || 'Apply';
    }
  }

  function showMessage(el, text, type) {
    if (!el) return;
    if (type === 'clear' || !text) {
      el.hidden = true;
      el.textContent = '';
      el.classList.remove('is-success', 'is-error');
      return;
    }
    el.hidden = false;
    el.textContent = text;
    el.classList.toggle('is-success', type === 'success');
    el.classList.toggle('is-error', type === 'error');
  }

  async function refreshCartDrawer() {
    // Try the standard Dawn pattern first.
    try {
      const sectionId =
        document.querySelector('cart-drawer')?.dataset?.sectionId ||
        document.querySelector('[id^="shopify-section-cart-drawer"]')?.id?.replace('shopify-section-', '');

      if (!sectionId) {
        window.location.reload();
        return;
      }

      const res = await fetch(`${window.location.pathname}?section_id=${sectionId}`, {
        credentials: 'same-origin',
      });
      const html = await res.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newDrawer = doc.querySelector('cart-drawer');
      const currentDrawer = document.querySelector('cart-drawer');
      if (newDrawer && currentDrawer) {
        currentDrawer.innerHTML = newDrawer.innerHTML;
      } else {
        // Fallback to reload
        window.location.reload();
      }
    } catch (err) {
      console.error('[cart-discount] drawer refresh failed', err);
      window.location.reload();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
